// ** React Imports
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Row, Col, Spinner, Alert } from "reactstrap";
import { Button } from "react-bootstrap";
import { useReactToPrint } from "react-to-print";
import toast from 'react-hot-toast';
import moment from 'moment';

// Services
import { getCertificateByVerificationCodeService } from "../../../services/certificateService";
import ESGCertificate from "../pdfReport/ESGCertificate";

// Components
import NavbarComponent from "../../components/layouts/Navbar";
import Footer from '../../components/layouts/Footer';

const CertificateVerification = () => {
    const { verificationCode } = useParams();
    const navigate = useNavigate();
    const certificateRef = useRef();
    const [loading, setLoading] = useState(true);
    const [certificateData, setCertificateData] = useState(null);
    const [error, setError] = useState(null);
    const [pdfContent, setPdfContent] = useState(false);

    useEffect(() => {
        const fetchCertificate = async () => {
            try {
                setLoading(true);
                const result = await getCertificateByVerificationCodeService({ verificationCode });
                
                if (result?.data?.success && result?.data?.data) {
                    setCertificateData(result.data.data);
                } else {
                    setError('Certificate not found or invalid verification code.');
                }
            } catch (err) {
                console.error('Error fetching certificate:', err);
                setError(err?.response?.data?.message || 'Failed to load certificate. Please check the verification code.');
            } finally {
                setLoading(false);
            }
        };

        if (verificationCode) {
            fetchCertificate();
        } else {
            setError('Invalid verification code provided.');
            setLoading(false);
        }
    }, [verificationCode]);

    // Print handler
    const handlePrint = useReactToPrint({
        content: () => certificateRef.current,
        documentTitle: `${certificateData?.certificateData?.profileData?.company || 'Company'}_ESG_Certificate`,
        onAfterPrint: () => {
            setPdfContent(false);
        }
    });

    const handleDownload = () => {
        setPdfContent(true);
        setTimeout(() => {
            handlePrint();
        }, 500);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <Spinner color="primary" />
            </div>
        );
    }

    if (error) {
        return (
            <>
                <NavbarComponent />
                <Container className="mt-5 mb-5">
                    <Row className="justify-content-center">
                        <Col md="8">
                            <Alert color="danger">
                                <h4>Certificate Not Found</h4>
                                <p>{error}</p>
                                <Button color="primary" onClick={() => navigate('/')}>
                                    Go to Home
                                </Button>
                            </Alert>
                        </Col>
                    </Row>
                </Container>
                <Footer />
            </>
        );
    }

    return (
        <>
            <NavbarComponent />
            <Container className="mt-5 mb-5">
                <Row className="justify-content-center">
                    <Col md="10">
                        <div className="mb-4">
                            <h2 className="text-center mb-3">ESG Certificate Verification</h2>
                            <div className="text-center mb-4">
                                <p className="text-muted">
                                    <strong>Company:</strong> {certificateData?.certificateData?.profileData?.company}<br />
                                    <strong>Verification Code:</strong> {verificationCode}<br />
                                    <strong>Issued Date:</strong> {certificateData?.certificateData?.issuedDate ? moment(certificateData.certificateData.issuedDate).format('DD MMMM YYYY') : 'N/A'}
                                </p>
                                <Button 
                                    variant="primary" 
                                    onClick={handleDownload}
                                    className="mt-3"
                                >
                                    Download Certificate
                                </Button>
                            </div>
                        </div>

                        {/* Certificate Display */}
                        <div style={{ 
                            background: '#f8f9fa', 
                            padding: '20px', 
                            borderRadius: '8px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                        }}>
                            <ESGCertificate
                                ref={certificateRef}
                                pdfContent={pdfContent}
                                profileData={certificateData?.certificateData?.profileData}
                                ESGperformanceData={certificateData?.certificateData?.ESGperformanceData}
                                indicatorData={certificateData?.certificateData?.indicatorData}
                                overallScorePercent={certificateData?.certificateData?.overallScorePercent}
                                verificationUrl={certificateData?.verificationUrl || window.location.href}
                            />
                        </div>

                        {/* Verification Info */}
                        <div className="mt-4 p-3 bg-light rounded">
                            <h5>Certificate Verification</h5>
                            <p className="mb-2">
                                <strong>This certificate is verified and authentic.</strong>
                            </p>
                            <p className="mb-0 small text-muted">
                                Verification Code: <code>{verificationCode}</code><br />
                                Share this link to verify: <code>{window.location.href}</code>
                            </p>
                        </div>
                    </Col>
                </Row>
            </Container>
            <Footer />
        </>
    );
};

export default CertificateVerification;

